/**
 * @file table-pagination.ts
 * @module app/modules/_common/hooks/table-pagination
 * @description Shared page/pageLimit state and next/previous/current/limit handlers,
 * extracted from the near-identical pagination boilerplate repeated across list modules.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.1
 * @version 1.3.1
 */

import { useState } from 'react';
import { PAGE_LIMIT } from '../../../../utils/constants';

export interface IUsePaginationResult {
  page: number;
  pageLimit: number;
  setPage: (page: number) => void;
  setPageLimit: (limit: number) => void;
  handleCurrentPage: (val: number) => Promise<void>;
  handleNextPage: (val: number) => Promise<void>;
  handlePreviousPage: (val: number) => Promise<void>;
  handlePageLimit: (event: any) => Promise<void>;
}

/**
 * @param onPageChange called with the resolved page/limit after each pagination action
 * @param initialLimit initial page size, defaults to the app-wide `PAGE_LIMIT`
 * @param initialPage initial page number, defaults to 1
 */
export const usePagination = (
  onPageChange: (page: number, limit: number) => void | Promise<void>,
  initialLimit: number = PAGE_LIMIT,
  initialPage: number = 1
): IUsePaginationResult => {
  const [page, setPage] = useState(initialPage);
  const [pageLimit, setPageLimit] = useState(initialLimit);

  const handleCurrentPage = async (val: number) => {
    setPage(val);
    await onPageChange(val, pageLimit);
  };

  const handleNextPage = async (val: number) => {
    setPage(val + 1);
    await onPageChange(val + 1, pageLimit);
  };

  const handlePreviousPage = async (val: number) => {
    setPage(val - 1);
    await onPageChange(val - 1, pageLimit);
  };

  const handlePageLimit = async (event: any) => {
    setPage(1);
    setPageLimit(event.target.value);
    await onPageChange(1, event.target.value);
  };

  return {
    page,
    pageLimit,
    setPage,
    setPageLimit,
    handleCurrentPage,
    handleNextPage,
    handlePreviousPage,
    handlePageLimit,
  };
};
